import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { userApi } from '../../../services/api';
import { 
  FiUsers, 
  FiUserPlus, 
  FiMail, 
  FiFilter, 
  FiSearch,
  FiEdit3,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiChevronDown,
  FiChevronRight,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiSettings,
  FiSun
} from 'react-icons/fi';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import FranchiseAssignmentModal from '../../../components/dashboard/BlockManagement/components/FranchiseAssignmentModal/FranchiseAssignmentModal';
import './UserManagement.css';

const UserManagement = () => {
  const { userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [availableBlocks, setAvailableBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, administrators, franchisees, operators
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFranchise, setSelectedFranchise] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showFranchiseAssignmentModal, setShowFranchiseAssignmentModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});

  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'operator',
    franchiseId: '',
    displayName: '',
    permissions: {},
    preAssignedBlockSerial: '' // New field for block pre-assignment
  });

  // Role edit form state
  const [roleForm, setRoleForm] = useState({
    role: '',
    franchiseId: '',
    permissions: {}
  });

  // All user management functions now use the centralized userApi from api.js

  // Fetch data
  const fetchUsers = useCallback(async () => {
    try {
      // Don't order by metadata.createdAt as it might not exist for all users
      // Instead, fetch all users and sort client-side
      const usersQuery = query(
        collection(db, 'users')
      );
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort client-side by email (which all users should have) as fallback
      const sortedUsers = usersData.sort((a, b) => {
        const aCreatedAt = a.metadata?.createdAt || a.createdAt || new Date().toISOString();
        const bCreatedAt = b.metadata?.createdAt || b.createdAt || new Date().toISOString();
        return new Date(bCreatedAt) - new Date(aCreatedAt);
      });
      
      setUsers(sortedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    }
  }, []);

  const fetchFranchises = useCallback(async () => {
    try {
      const franchisesQuery = query(
        collection(db, 'franchises'),
        orderBy('name')
      );
      const franchisesSnapshot = await getDocs(franchisesQuery);
      const franchisesData = franchisesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFranchises(franchisesData);
    } catch (err) {
      console.error('Error fetching franchises:', err);
      setError('Failed to load franchises');
    }
  }, []);

  // Fetch available blocks for a specific franchise (for pre-assignment)
  const fetchAvailableBlocks = useCallback(async (franchiseId) => {
    if (!franchiseId) {
      setAvailableBlocks([]);
      return;
    }
    
    try {
      // Fetch blocks assigned to this franchise that don't already have an operator
      const blocksQuery = query(
        collection(db, 'blocks'),
        where('assignedFranchise', '==', franchiseId)
      );
      const blocksSnapshot = await getDocs(blocksQuery);
      const blocksData = blocksSnapshot.docs.map(doc => ({
        id: doc.id,
        serial: doc.id, // Document ID is the serial
        ...doc.data()
      }));
      
      // Filter out blocks that already have an assigned operator
      const unassignedBlocks = blocksData.filter(block => 
        !block.assignedOperator || block.assignedOperator === ''
      );
      
      setAvailableBlocks(unassignedBlocks);
    } catch (err) {
      console.error('Error fetching available blocks:', err);
      setAvailableBlocks([]);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchFranchises()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchFranchises]);

  // Filter and search users
  const filteredUsers = users.filter(user => {
    // Role filter
    if (filter !== 'all' && user.globalRole !== filter.slice(0, -1)) {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        user.email?.toLowerCase().includes(searchLower) ||
        user.displayName?.toLowerCase().includes(searchLower)
      );
    }

    // Franchise filter
    if (selectedFranchise !== 'all') {
      return user.franchiseId === selectedFranchise;
    }

    return true;
  });

  // Group users by role for display
  const groupedUsers = filteredUsers.reduce((acc, user) => {
    const role = user.globalRole || 'pending';
    if (!acc[role]) acc[role] = [];
    acc[role].push(user);
    return acc;
  }, {});

  // Handle invite user
  const handleInviteUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const invitationData = {
        email: inviteForm.email,
        role: inviteForm.role,
        franchiseId: inviteForm.franchiseId || null,
        displayName: inviteForm.displayName,
        permissions: getDefaultPermissions(inviteForm.role)
      };
      
      // Add block pre-assignment for operators
      if (inviteForm.role === 'operator' && inviteForm.preAssignedBlockSerial) {
        invitationData.preAssignedBlockSerial = inviteForm.preAssignedBlockSerial;
      }
      
      const result = await userApi.createUserInvitation(invitationData);

      if (result.success) {
        setShowInviteModal(false);
        setInviteForm({
          email: '',
          role: 'operator',
          franchiseId: '',
          displayName: '',
          permissions: {},
          preAssignedBlockSerial: ''
        });
        setAvailableBlocks([]); // Clear available blocks
        await fetchUsers();
        alert('Invitation sent successfully!' + 
          (invitationData.preAssignedBlockSerial ? ` Block ${invitationData.preAssignedBlockSerial} has been pre-assigned.` : ''));
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      
      // Handle specific error codes gracefully
      if (err.code === 'functions/invalid-argument' && err.message.includes('Invitation already sent')) {
        // User already has a pending invitation
        setError(`⚠️ An invitation has already been sent to ${inviteForm.email}. Please check if they received it or contact them directly.`);
      } else if (err.code === 'error_409' || err.message.includes('Invitation already sent')) {
        // Alternative 409 error format
        setError(`⚠️ An invitation has already been sent to ${inviteForm.email}. Please check if they received it or resend if needed.`);
      } else if (err.code === 'functions/permission-denied') {
        setError('❌ You do not have permission to send invitations. Please contact an administrator.');
      } else if (err.code === 'functions/invalid-argument') {
        setError('❌ Invalid invitation data. Please check all required fields.');
      } else if (err.message.includes('required')) {
        setError('❌ Missing required fields. Please fill out all required information.');
      } else {
        setError('❌ Failed to send invitation: ' + (err.message || 'Unknown error occurred'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle user actions
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await userApi.updateUserRole(userId, { role: newRole });
      await fetchUsers();
      alert('User role updated successfully!');
    } catch (err) {
      console.error('Error updating user role:', err);
      setError('Failed to update user role');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      await userApi.deactivateUser(userId);
      await fetchUsers();
      alert('User deactivated successfully!');
    } catch (err) {
      console.error('Error deactivating user:', err);
      setError('Failed to deactivate user');
    }
  };

  const handleResendInvitation = async (email) => {
    try {
      await userApi.resendInvitation(email);
      alert('Invitation resent successfully!');
    } catch (err) {
      console.error('Error resending invitation:', err);
      setError('Failed to resend invitation');
    }
  };

  const handleActivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to activate this user?')) {
      return;
    }

    try {
      setLoading(true);
      await userApi.activateUser(userId);
      await fetchUsers();
      alert('User activated successfully!');
    } catch (err) {
      console.error('Error activating user:', err);
      setError('Failed to activate user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    const confirmMessage = `WARNING: This will permanently delete the user account.\n\nType "${userEmail}" to confirm deletion:`;
    const confirmation = window.prompt(confirmMessage);
    
    if (confirmation !== userEmail) {
      if (confirmation !== null) {
        alert('Deletion cancelled - email confirmation did not match.');
      }
      return;
    }

    try {
      setLoading(true);
      await userApi.deleteUser(userId, userEmail);
      await fetchUsers();
      alert('User deleted successfully!');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRoleModal = (user) => {
    setSelectedUser(user);
    setRoleForm({
      role: user.globalRole || '',
      franchiseId: user.franchiseId || '',
      permissions: user.permissions || getDefaultPermissions(user.globalRole || 'operator')
    });
    setShowRoleModal(true);
  };

  const handleUpdateUserRoleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setLoading(true);
      await updateUserRole({
        userId: selectedUser.id,
        role: roleForm.role,
        permissions: roleForm.permissions
      });
      
      setShowRoleModal(false);
      setSelectedUser(null);
      setRoleForm({ role: '', franchiseId: '', permissions: {} });
      await fetchUsers();
      alert('User role updated successfully!');
    } catch (err) {
      console.error('Error updating user role:', err);
      setError('Failed to update user role: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get default permissions for role
  const getDefaultPermissions = (role) => {
    const basePermissions = {
      viewBlocks: true,
      updateBlockStatus: false,
      manageRefills: false,
      logMaintenance: false,
      viewAnalytics: false,
      exportData: false,
      primeDevice: false,
      resetDevice: false,
      manageSensors: false,
      confirmPayments: false,
      updateLocation: false,
      updateAddress: false,
      manageImages: false,
      setFreeMode: false,
      deleteBlocks: false,
      manageUsers: false
    };

    switch (role) {
      case 'administrator':
        return Object.keys(basePermissions).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {});

      case 'franchisee':
        return {
          ...basePermissions,
          updateBlockStatus: true,
          manageRefills: true,
          logMaintenance: true,
          viewAnalytics: true,
          exportData: true,
          primeDevice: true,
          confirmPayments: true,
          updateLocation: true,
          updateAddress: true,
          manageImages: true,
          manageUsers: true
        };

      case 'operator':
        return {
          ...basePermissions,
          updateBlockStatus: true,
          manageRefills: true,
          logMaintenance: true,
          primeDevice: true
        };

      default:
        return basePermissions;
    }
  };

  const toggleUserExpansion = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'administrator': return 'admin';
      case 'franchisee': return 'franchisee';
      case 'operator': return 'operator';
      default: return 'pending';
    }
  };

  const getStatusIcon = (user) => {
    if (!user.isActive) return <FiEyeOff className="status-icon inactive" />;
    if (user.globalRole === 'pending') return <FiClock className="status-icon pending" />;
    return <FiCheckCircle className="status-icon active" />;
  };

  if (loading && users.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-title">
          <FiUsers className="header-icon" />
          <h1>User Management</h1>
          <span className="user-count">{users.length} total users</span>
        </div>
        
        <div className="header-actions">
          <button 
            className="invite-user-btn"
            onClick={() => setShowInviteModal(true)}
          >
            <FiUserPlus />
            Invite User
          </button>
          
          <button 
            className="franchise-assignment-btn"
            onClick={() => setShowFranchiseAssignmentModal(true)}
          >
            <FiSun />
            Assign Franchises
          </button>
        </div>
      </div>

      {error && (
        <div className="error-alert">
          <FiAlertCircle />
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="user-filters">
        <div className="filter-group">
          <div className="filter-icon-wrapper">
            <FiFilter className="filter-icon" />
          </div>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Roles</option>
            <option value="administrators">Administrators</option>
            <option value="franchisees">Franchisees</option>
            <option value="operators">Operators</option>
          </select>
        </div>

        <div className="filter-group">
          <select 
            value={selectedFranchise} 
            onChange={(e) => setSelectedFranchise(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Franchises</option>
            {franchises.map(franchise => (
              <option key={franchise.id} value={franchise.id}>
                {franchise.name}
              </option>
            ))}
          </select>
        </div>

        <div className="search-group">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="users-list">
        {Object.entries(groupedUsers).map(([role, roleUsers]) => (
          <div key={role} className="role-group">
            <div className="role-header">
              <h3 className={`role-title ${getRoleColor(role)}`}>
                {role.charAt(0).toUpperCase() + role.slice(1)}s
                <span className="role-count">({roleUsers.length})</span>
              </h3>
            </div>

            <div className="users-grid">
              {roleUsers.map(user => (
                <div key={user.id} className="user-card">
                  <div className="user-card-header" onClick={() => toggleUserExpansion(user.id)}>
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                      </div>
                      <div className="user-details">
                        <h4 className="user-name">
                          {user.displayName || 'No name'}
                          {getStatusIcon(user)}
                        </h4>
                        <p className="user-email">{user.email}</p>
                        <span className={`user-role-badge ${getRoleColor(user.globalRole)}`}>
                          {user.globalRole || 'pending'}
                        </span>
                      </div>
                    </div>
                    <div className="user-expand-icon">
                      {expandedUsers[user.id] ? <FiChevronDown /> : <FiChevronRight />}
                    </div>
                  </div>

                  {expandedUsers[user.id] && (
                    <div className="user-card-expanded">
                      <div className="user-metadata">
                        <div className="metadata-item">
                          <strong>Created:</strong> 
                          {user.metadata?.createdAt ? 
                            new Date(user.metadata.createdAt.toDate()).toLocaleDateString() : 
                            'Unknown'
                          }
                        </div>
                        <div className="metadata-item">
                          <strong>Last Login:</strong> 
                          {user.metadata?.lastLogin ? 
                            new Date(user.metadata.lastLogin.toDate()).toLocaleDateString() : 
                            'Never'
                          }
                        </div>
                        <div className="metadata-item">
                          <strong>Status:</strong> 
                          <span className={user.isActive ? 'status-active' : 'status-inactive'}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      <div className="user-actions">
                        <button 
                          className="action-btn edit"
                          onClick={() => handleOpenRoleModal(user)}
                        >
                          <FiEdit3 />
                          Edit Role
                        </button>
                        
                        {user.globalRole === 'pending' && (
                          <button 
                            className="action-btn resend"
                            onClick={() => handleResendInvitation(user.email)}
                          >
                            <FiMail />
                            Resend Invite
                          </button>
                        )}
                        
                        <button 
                          className="action-btn settings"
                          onClick={() => {/* Handle permissions */}}
                        >
                          <FiSettings />
                          Permissions
                        </button>
                        
                        {user.isActive ? (
                          <button 
                            className="action-btn warning"
                            onClick={() => handleDeactivateUser(user.id)}
                          >
                            <FiEyeOff />
                            Deactivate
                          </button>
                        ) : (
                          user.globalRole !== 'pending' && (
                            <button 
                              className="action-btn success"
                              onClick={() => handleActivateUser(user.id)}
                            >
                              <FiEye />
                              Activate
                            </button>
                          )
                        )}
                        
                        <button 
                          className="action-btn danger"
                          onClick={() => handleDeleteUser(user.id, user.email)}
                        >
                          <FiTrash2 />
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Invite New User</h3>
              <button 
                className="modal-close"
                onClick={() => setShowInviteModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleInviteUser} className="invite-form">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                  required
                  placeholder="user@example.com"
                />
              </div>

              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={inviteForm.displayName}
                  onChange={(e) => setInviteForm(prev => ({
                    ...prev,
                    displayName: e.target.value
                  }))}
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm(prev => ({
                    ...prev,
                    role: e.target.value
                  }))}
                  required
                >
                  <option value="operator">Operator</option>
                  <option value="franchisee">Franchisee</option>
                  <option value="administrator">Administrator</option>
                </select>
              </div>

              {inviteForm.role !== 'administrator' && (
                <div className="form-group">
                  <label>Franchise</label>
                  <select
                    value={inviteForm.franchiseId}
                    onChange={(e) => {
                      const franchiseId = e.target.value;
                      setInviteForm(prev => ({
                        ...prev,
                        franchiseId,
                        preAssignedBlockSerial: '' // Reset block selection when franchise changes
                      }));
                      // Fetch available blocks for the selected franchise
                      if (franchiseId && inviteForm.role === 'operator') {
                        fetchAvailableBlocks(franchiseId);
                      } else {
                        setAvailableBlocks([]);
                      }
                    }}
                    required={inviteForm.role !== 'administrator'}
                  >
                    <option value="">Select a franchise</option>
                    {franchises.map(franchise => (
                      <option key={franchise.id} value={franchise.id}>
                        {franchise.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {inviteForm.role === 'operator' && inviteForm.franchiseId && (
                <div className="form-group">
                  <label>
                    Pre-Assign Block (Optional)
                    <span className="form-help">Select a block to automatically assign to this operator upon invitation acceptance</span>
                  </label>
                  <select
                    value={inviteForm.preAssignedBlockSerial}
                    onChange={(e) => setInviteForm(prev => ({
                      ...prev,
                      preAssignedBlockSerial: e.target.value
                    }))}
                  >
                    <option value="">No pre-assignment</option>
                    {availableBlocks.map(block => (
                      <option key={block.serial} value={block.serial}>
                        {block.serial} - {block.location || 'Unknown Location'} 
                        {block.address ? ` (${block.address})` : ''}
                      </option>
                    ))}
                  </select>
                  {availableBlocks.length === 0 && inviteForm.franchiseId && (
                    <div className="form-help">
                      No unassigned blocks available in this franchise territory.
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowInviteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Role Edit Modal */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit User Role</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                  setRoleForm({ role: '', franchiseId: '', permissions: {} });
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateUserRoleSubmit} className="invite-form">
              <div className="form-group">
                <label>User</label>
                <div style={{ 
                  padding: '12px', 
                  background: '#f9fafb', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '6px',
                  color: '#6b7280'
                }}>
                  {selectedUser.displayName || 'No name'} ({selectedUser.email})
                </div>
              </div>

              <div className="form-group">
                <label>New Role</label>
                <select
                  value={roleForm.role}
                  onChange={(e) => setRoleForm(prev => ({
                    ...prev,
                    role: e.target.value,
                    permissions: getDefaultPermissions(e.target.value)
                  }))}
                  required
                >
                  <option value="">Select a role</option>
                  <option value="operator">Operator</option>
                  <option value="franchisee">Franchisee</option>
                  <option value="administrator">Administrator</option>
                </select>
              </div>

              {roleForm.role && roleForm.role !== 'administrator' && (
                <div className="form-group">
                  <label>Franchise</label>
                  <select
                    value={roleForm.franchiseId}
                    onChange={(e) => setRoleForm(prev => ({
                      ...prev,
                      franchiseId: e.target.value
                    }))}
                    required={roleForm.role !== 'administrator'}
                  >
                    <option value="">Select a franchise</option>
                    {franchises.map(franchise => (
                      <option key={franchise.id} value={franchise.id}>
                        {franchise.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                    setRoleForm({ role: '', franchiseId: '', permissions: {} });
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Franchise Assignment Modal */}
      <FranchiseAssignmentModal
        isOpen={showFranchiseAssignmentModal}
        onClose={() => setShowFranchiseAssignmentModal(false)}
        operators={users}
        franchises={franchises}
        onAssignmentUpdate={async () => {
          await fetchUsers();
          await fetchFranchises();
        }}
      />
    </div>
  );
};

export default UserManagement; 